import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ProductData, UploadUrl } from '../../../models';
import { ListingService } from '../../../services/listing.service';
import { firstValueFrom } from 'rxjs';
import { isErrorResponse } from '../../../services/base.service';
import { ActivatedRoute } from '@angular/router';
import { UploadService } from '../../../services/upload.service';
import { HttpEventType, HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { nanoid } from 'nanoid';
import * as L from 'leaflet';
import { ToastService } from '../../../services/toast.service';

@Component({
  selector: 'app-product-listing',
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './create-or-update.component.html',
  styleUrl: './create-or-update.component.css'
})
export class CreateOrUpdateComponent implements OnInit, AfterViewInit, OnDestroy {

  categories: string[] = ['kids', 'beauty', 'food', 'mens', "womens", 'electronics', 'home', 'others'];

  productData: Partial<ProductData> = {
    title: '',
    name: '',
    price: 0,
    listingPrice: 0,
    discount: 0,
    sku: nanoid(),
    category: this.categories[0],
    summary: '',
    description: '',
    imageUrl: '',
    notes: '',
    currency: '₹',
  };
  placementOptions: string[] = ['Homepage', 'Sidebar', 'Deals Section'];
  loading = false;
  message = '';
  messageType: 'success' | 'error' = 'success';
  uploadedFile: File | null = null;
  uploadUrl = '';
  uploading = true;
  uploadPercent = 0;
  imageId: string | null = null;

  listingId: string | null = null;
  editing = false;

  private map: L.Map | undefined;
  private marker: L.Marker | undefined;
  searchQuery: string = '';

  constructor(
    private productService: ListingService,
    private uploadService: UploadService,
    private route: ActivatedRoute,
    private http: HttpClient,
    private toast: ToastService
  ) {
  }

  ngOnInit() {
    // Check route parameters (e.g., /reveal/:id)
    this.route.params.subscribe(params => {
      this.listingId = params['id'];
      if (this.listingId) {
        this.productService.get(this.listingId).subscribe((listing: any) => {
          if (listing) {
            this.productData = {
              id: listing.id,
              type: listing.type,
              title: listing.title,
              name: listing.name,
              price: listing.price,
              listingPrice: listing.listingPrice,
              discount: listing.discount,
              sku: listing.sku ? listing.sku : nanoid(),
              currency: listing.currency,
              category: listing.category || this.categories[0],
              summary: listing.summary,
              description: listing.description,
              imageUrl: listing.imageUrl,
              extraImageUrls: listing.extraImageUrls,
              externalDealLink: listing.externalDealLink,
              externalProductLink: listing.externalProductLink,
              createdAt: listing.createdAt || listing.timestampedAt,
              expiresAt: listing.expiresAt,
              notes: listing.notes,
              updatedBy: listing.updatedBy,
              updatedAt: listing.updatedAt,
              location: listing.location
            };
            this.editing = true;
          }
        });
      } else {
        // Ensure category is selected for new items
        if (!this.productData.category) {
          this.productData.category = this.categories[0];
        }
      }
    });
  }

  ngAfterViewInit(): void {
    this.initMap();
  }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.remove();
    }
  }

  private initMap(): void {
    // Default to some location if none setting (Example: London or User's current location if possible)
    // For now, let's default to a neutral view or coordinate 0,0 zoom 2
    const defaultLat = 51.505;
    const defaultLng = -0.09;
    const zoomLevel = 13;

    let lat = defaultLat;
    let lng = defaultLng;

    if (this.productData.location && this.productData.location.length === 2) {
      lat = this.productData.location[0];
      lng = this.productData.location[1];
    } else {
      // Auto-detect location if not set
      this.locateMe();
    }

    this.map = L.map('map').setView([lat, lng], zoomLevel);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap'
    }).addTo(this.map);

    // Fix for default marker icon issues in Leaflet with webpack/angular
    // using CDN to ensure icons are always available
    const iconRetinaUrl = 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png';
    const iconUrl = 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png';
    const shadowUrl = 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png';
    const iconDefault = L.icon({
      iconRetinaUrl,
      iconUrl,
      shadowUrl,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      tooltipAnchor: [16, -28],
      shadowSize: [41, 41]
    });
    L.Marker.prototype.options.icon = iconDefault;


    if (this.productData.location && this.productData.location.length === 2) {
      this.marker = L.marker([lat, lng]).addTo(this.map);
    }

    this.map.on('click', (e: L.LeafletMouseEvent) => {
      const { lat, lng } = e.latlng;
      this.productData.location = [lat, lng];

      if (this.marker) {
        this.marker.setLatLng(e.latlng);
      } else {
        this.marker = L.marker(e.latlng).addTo(this.map!);
      }
    });

    // Need to invalidate size after a delay if in a tab or modal, but here it's main page
    setTimeout(() => {
      this.map?.invalidateSize();
    }, 100);
  }


  locateMe() {
    if (navigator.geolocation) {
      this.loading = true; // reusing loading spinner or logic
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          this.productData.location = [lat, lng];

          if (this.map) {
            this.map.setView([lat, lng], 16);
            if (this.marker) {
              this.marker.setLatLng([lat, lng]);
            } else {
              this.marker = L.marker([lat, lng]).addTo(this.map);
            }
          }
          this.loading = false;
        },
        (error) => {
          console.error("Geolocation error", error);
          this.message = "Unable to retrieve your location";
          this.messageType = 'error';
          this.loading = false;
        }
      );
    } else {
      this.message = "Geolocation is not supported by this browser.";
      this.messageType = 'error';
    }
  }

  searchLocation() {
    if (!this.searchQuery) return;

    // Use OpenStreetMap Nominatim API
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(this.searchQuery)}`;

    this.http.get(url).subscribe((results: any) => {
      if (results && results.length > 0) {
        const firstResult = results[0];
        const lat = parseFloat(firstResult.lat);
        const lng = parseFloat(firstResult.lon);

        // Move map
        this.productData.location = [lat, lng];
        if (this.map) {
          this.map.setView([lat, lng], 15);
          if (this.marker) {
            this.marker.setLatLng([lat, lng]);
          } else {
            this.marker = L.marker([lat, lng]).addTo(this.map);
          }
        }
      } else {
        this.message = "Location not found";
        this.messageType = 'error';
      }
    });
  }

  loadCategories() {
    // Example: get from API or service
    this.categories = ['kids', 'beauty', 'food', 'cloth', 'mens'];
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onFileDrop(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer?.files?.length) {
      this.startUpload(event.dataTransfer.files[0]);
    }
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    this.startUpload(file);
  }

  async startUpload(file: File) {
    if (!file) return;
    this.uploading = true;
    this.uploadPercent = 0;
    let imageKey = '';

    try {
      if (!this.uploadUrl) {
        await firstValueFrom(this.uploadService.getUploadUrl()).then((response: UploadUrl) => {
          this.uploadUrl = response.uploadUrl;
          imageKey = response.id;
        });
      }

      this.uploadService.uploadWithProgress(file, this.uploadUrl).subscribe({
        next: (event: any) => {
          if (event.type === HttpEventType.UploadProgress) {
            this.uploadPercent = Math.round((event.loaded / event.total!) * 100);
          }

          if (event.type === HttpEventType.Response) {
            this.uploading = false;
            this.imageId = imageKey;
            this.productData.imageUrl = environment.apiBaseUrl + '/media/' + imageKey;
            this.uploadUrl = '';
          }
        },
        error: (err) => {
          console.error('Upload error', err);
          this.uploading = false;
        }
      });

    } catch (err) {
      console.error(err);
      this.uploading = false;
    }
  }

  onGetUploadUrl() {
    this.uploadService.getUploadUrl().subscribe(response => {
      this.uploadUrl = response.uploadUrl;
    },
      error => {
        console.error('Error fetching upload URL:', error);
      });
  }

  async onSubmit(event: Event) {
    event.preventDefault();

    if (!this.validateForm()) {
      return;
    }

    this.loading = true;
    this.message = '';

    try {
      if (!this.productData.createdAt) {
        this.productData.createdAt = new Date().toISOString();
      }
      if (this.editing && this.listingId) {
        const response = await firstValueFrom(this.productService.update(this.listingId, this.productData));
        if (isErrorResponse(response)) {
          throw new Error(response.error || 'Unable to update product');
        }
      } else {
        const response = await firstValueFrom(this.productService.create(this.productData));
        if (isErrorResponse(response)) {
          throw new Error(response.error || 'Unable to create product');
        }
        this.resetForm();
      }

      this.toast.success('Product saved successfully!');
    } catch (error: any) {
      this.toast.error(error.message || 'Failed to save product');
    } finally {
      this.loading = false;
    }
  }

  private validateForm(): boolean {
    if (!this.productData.name || !this.productData.name.trim()) {
      this.toast.error('Product name is required');
      return false;
    }


    if (!this.productData.price || this.productData.price <= 0) {
      this.toast.error('Price must be greater than 0');
      return false;
    }
    return true;
  }

  private resetForm() {
    this.productData = {
      id: '',
      type: '',
      name: '',
      price: 0,
      listingPrice: 0,
      currency: 'USD',
      category: this.categories[0],
      summary: '',
      description: '',
      imageUrl: '',
      extraImageUrls: [],
      externalDealLink: undefined,
      externalProductLink: '',
      createdAt: '',
      expiresAt: undefined,
      notes: '',
      location: undefined
    };
    if (this.marker) {
      this.marker.remove();
      this.marker = undefined;
    }
  }
}
